import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatDate, formatDateTime } from "../../lib/api";
import { toast } from "sonner";
import { QrCode, ArrowsClockwise, Gear, CheckCircle, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_LABEL = {
  criado: { label: "Criado", cls: "neutral" },
  aguardando_pagamento: { label: "Aguardando", cls: "medio" },
  pago: { label: "Pago", cls: "green" },
  expirado: { label: "Expirado", cls: "risco" },
  cancelado: { label: "Cancelado", cls: "red" },
  erro: { label: "Erro", cls: "red" },
};

function ChargeStatus({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: "neutral" };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export default function AdminPix() {
  const [charges, setCharges] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState("");
  const [monitor, setMonitor] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reprocessing, setReprocessing] = useState("");

  const load = async () => setCharges((await api.get("/pix/charges")).data);
  const loadMonitor = async () => {
    try { setMonitor((await api.get("/mercadopago/monitor")).data); } catch { /* silent */ }
  };
  useEffect(() => { load(); loadMonitor(); }, []);

  // Baixa automatica: a tela reflete os pagamentos sem clique manual.
  useEffect(() => {
    const t = setInterval(() => { load(); loadMonitor(); }, 15000);
    return () => clearInterval(t);
  }, []);

  const runReconciliation = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/mercadopago/reconciliation/run");
      toast.success(`Conciliacao concluida · ${data.applied || 0} baixa(s), ${data.checked || 0} verificada(s)`);
      load(); loadMonitor();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na conciliacao");
    } finally { setReconciling(false); }
  };

  const reprocessEvent = async (eventId) => {
    setReprocessing(eventId);
    try {
      const { data } = await api.post(`/mercadopago/reprocess/${eventId}`);
      toast.success(`Reprocessado: ${data.reason}`);
      load(); loadMonitor();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reprocessar");
    } finally { setReprocessing(""); }
  };

  const EVENT_LABEL = {
    no_installment: "Aprovado sem parcela localizada",
    divergence: "Divergencia de valor",
    fetch_error: "Falha temporaria na consulta",
    apply_error: "Falha ao registrar baixa",
    no_token: "Sem token da conta Mercado Pago",
    webhook_error: "Erro no webhook",
    empty_response: "Resposta vazia do Mercado Pago",
    refunded: "Estorno recebido",
    charged_back: "Chargeback recebido",
  };

  const processUpcoming = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/pix/process-upcoming");
      if (data.ok === false) {
        toast.error(data.detail || "Nao foi possivel gerar as cobrancas");
      } else if (data.generated > 0) {
        toast.success(`${data.generated} cobranca(s) PIX gerada(s)`);
      } else if (data.skipped > 0) {
        toast.info(`${data.skipped} parcela(s) ja possuem cobranca aberta`);
      } else {
        toast.info("Nenhuma parcela vencendo nos proximos 3 dias");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao processar");
    } finally { setProcessing(false); }
  };

  const refreshStatus = async (chargeId) => {
    setRefreshing(chargeId);
    try {
      await api.post(`/pix/refresh/${chargeId}`);
      toast.success("Status atualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar");
    } finally { setRefreshing(""); }
  };

  const viewQr = async (installmentId) => {
    try {
      const { data } = await api.post(`/pix/charge/${installmentId}`);
      const w = window.open("", "_blank", "width=400,height=620");
      w.document.write(`
        <html><head><title>PIX Mercado Pago</title></head>
        <body style="font-family:system-ui;padding:24px;text-align:center;background:#F5F0EB">
          <h2 style="font-weight:700;letter-spacing:-0.02em">PIX Parcela</h2>
          <p style="font-size:24px;font-weight:700">R$ ${Number(data.amount).toFixed(2)}</p>
          <img src="${data.qr_code_data_url}" style="max-width:240px;border:1px solid #EBE5DF" />
          <p style="font-size:10px;color:#5C5A57;margin-top:16px">PIX COPIA E COLA</p>
          <textarea readonly style="width:100%;height:80px;font-size:11px;font-family:monospace">${data.copy_paste}</textarea>
          <p style="font-size:11px;color:#5C5A57;margin-top:12px">Mercado Pago · valido ate ${data.expires_at ? new Date(data.expires_at).toLocaleString("pt-BR") : "-"}</p>
        </body></html>
      `);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar PIX");
    }
  };

  return (
    <div data-testid="admin-pix-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Cobranca PIX automatica</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">PIX Parcelado</h1>
        </div>
        <button onClick={processUpcoming} disabled={processing} className="brand" data-testid="process-pix-btn">
          {processing ? "Processando..." : "Gerar PIX (proximos 3 dias)"}
        </button>
      </div>

      <div className="border border-[#EBE5DF] bg-[#F5F0EB] p-4 mb-6 text-sm flex items-center gap-2 flex-wrap">
        <Gear size={18} weight="duotone" />
        <span>As cobrancas usam o <strong>Mercado Pago</strong> conforme o modo configurado.</span>
        <Link to="/admin/configuracoes" className="text-xs tracking-widest uppercase font-medium underline" data-testid="pix-settings-link">Configurar integracao</Link>
      </div>

      {/* Painel de baixa automatica Mercado Pago */}
      <div className="border border-[#EBE5DF] bg-white p-5 mb-6" data-testid="mp-monitor-panel">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} weight="duotone" className="text-[#3B6E5E]" />
            <h2 className="font-display text-lg font-bold">Baixa automatica · Mercado Pago</h2>
          </div>
          <button onClick={runReconciliation} disabled={reconciling} className="brand disabled:opacity-50 flex items-center gap-2" data-testid="run-reconciliation-btn">
            <ArrowClockwise size={14} className={reconciling ? "animate-spin" : ""} />
            {reconciling ? "Conciliando..." : "Executar conciliacao agora"}
          </button>
        </div>
        <p className="text-xs text-[#5C5A57] mb-4">
          Pagamentos aprovados no Mercado Pago sao baixados automaticamente (webhook + conciliacao a cada poucos minutos).
          Esta tela atualiza sozinha; o botao "Atualizar status" e apenas contingencia.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="border border-[#EBE5DF] p-3" data-testid="mp-stat-processed">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Baixas automaticas</div>
            <div className="font-display text-2xl font-bold tabular">{monitor?.processed ?? "-"}</div>
          </div>
          <div className="border border-[#EBE5DF] p-3" data-testid="mp-stat-duplicates">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Duplicados ignorados</div>
            <div className="font-display text-2xl font-bold tabular">{monitor?.duplicates_ignored ?? "-"}</div>
          </div>
          <div className="border border-[#EBE5DF] p-3" data-testid="mp-stat-errors">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Pendentes / erro</div>
            <div className="font-display text-2xl font-bold tabular text-[#B4564F]">{monitor?.errors_count ?? "-"}</div>
          </div>
          <div className="border border-[#EBE5DF] p-3" data-testid="mp-stat-divergences">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Divergencias</div>
            <div className="font-display text-2xl font-bold tabular text-[#8B5E5E]">{monitor?.divergences ?? "-"}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-[#5C5A57] flex-wrap">
          <span>Ultimo evento: {monitor?.last_event_at ? formatDateTime(monitor.last_event_at) : "-"} {monitor?.last_event_type ? `(${monitor.last_event_type})` : ""}</span>
          <span>·</span>
          <span>Ultima conciliacao: {monitor?.last_reconciliation_at ? formatDateTime(monitor.last_reconciliation_at) : "-"}</span>
          <span>·</span>
          <span>Sem parcela: {monitor?.no_installment ?? 0}</span>
          <span>·</span>
          <span>Estornos: {monitor?.reversals ?? 0}</span>
        </div>

        {monitor?.recent_issues?.length > 0 && (
          <div className="mt-4 border-t border-[#EBE5DF] pt-3" data-testid="mp-issues-list">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2 flex items-center gap-1">
              <WarningCircle size={14} weight="duotone" className="text-[#B4564F]" /> Ocorrencias que precisam de atencao
            </div>
            <div className="space-y-2">
              {monitor.recent_issues.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between gap-3 text-xs border border-[#F0EBE5] p-2" data-testid={`mp-issue-${ev.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium">{EVENT_LABEL[ev.type] || ev.type}</span>
                    <span className="text-[#5C5A57]"> · pagamento {ev.mp_payment_id || "-"}{ev.amount ? ` · R$ ${Number(ev.amount).toFixed(2)}` : ""}</span>
                    {ev.detail && typeof ev.detail === "string" && <div className="text-[#8B5E5E] truncate">{ev.detail}</div>}
                  </div>
                  <button onClick={() => reprocessEvent(ev.id)} disabled={reprocessing === ev.id} className="text-[10px] tracking-widest uppercase font-medium text-[#3B6E5E] hover:underline shrink-0 flex items-center gap-1" data-testid={`mp-reprocess-${ev.id}`}>
                    <ArrowClockwise size={12} className={reprocessing === ev.id ? "animate-spin" : ""} /> Reprocessar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="table-wrap">
        <table className="crl">
          <thead><tr><th>Criado</th><th>Cliente</th><th>Valor</th><th>Origem</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {charges.map((c) => (
              <tr key={c.id}>
                <td className="text-[#5C5A57] text-xs">{formatDate(c.created_at)}</td>
                <td className="text-[#5C5A57] text-xs">{c.client_id?.slice(0, 8)}</td>
                <td className="tabular font-medium">{brl(c.amount)}</td>
                <td className="text-[#5C5A57] text-xs">{c.source === "reseller" ? "Revendedora" : "Plataforma"}</td>
                <td><ChargeStatus status={c.status} /></td>
                <td>
                  <div className="flex items-center gap-3">
                    <button onClick={() => viewQr(c.installment_id)} className="text-xs uppercase tracking-widest font-medium hover:underline flex items-center gap-1" data-testid={`view-qr-${c.id}`}>
                      <QrCode size={14} /> Ver QR
                    </button>
                    {c.mp_payment_id && (
                      <button onClick={() => refreshStatus(c.id)} disabled={refreshing === c.id} className="text-[#5C5A57] hover:text-black" title="Atualizar status" data-testid={`refresh-${c.id}`}>
                        <ArrowsClockwise size={16} className={refreshing === c.id ? "animate-spin" : ""} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {charges.length === 0 && (
              <tr><td colSpan="6" className="text-center text-sm text-[#5C5A57] py-8">
                Sem cobrancas. Clique em "Gerar PIX" para criar para parcelas que vencem em ate 3 dias.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
